import { MonthPicker } from "@caprikorn/dynamo-ui";
import type { RangeValues } from "@caprikorn/dynamo-ui/lib/components/dataEntry/Calendar/Calendar.types";
import { getDateShortLabel } from "@caprikorn/dynamo-utils";
import { type TableOptions, createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";

import type { CustomerBill } from "@services/apiEcms/apiEcms.types";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import { BILL_PAYMENT_STATUSES } from "./InvoiceAndPaymentsDetails.utils";

const columnHelper = createColumnHelper<CustomerBill>();

export const useDocsColumns = (): TableOptions<CustomerBill>["columns"] => {
  const { t } = useCustomTranslation(["main", "common"]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("accountingDocumentReference", {
        header: t(
          "perimeter.sites.invoiceAndPaymentsDetails.accountingDocumentsTable.columns.accountingDocumentReference",
        ),
        enableSorting: false,
      }),
      columnHelper.accessor("accountingDocumentSubtypeCode", {
        header: t(
          "perimeter.sites.invoiceAndPaymentsDetails.accountingDocumentsTable.columns.accountingDocumentSubtypeCode",
        ),
        enableSorting: false,
        cell: ({ getValue }) =>
          t(
            "perimeter.sites.invoiceAndPaymentsDetails.accountingDocumentsTable.accountingDocumentSubtype",
            { context: getValue() },
          ),
      }),
      columnHelper.accessor("accountingDocumentDate", {
        id: "accountingDocumentDate",
        header: t(
          "perimeter.sites.invoiceAndPaymentsDetails.accountingDocumentsTable.columns.accountingDocumentDate",
        ),
        enableSorting: false,
        cell: ({ getValue }) => {
          const createdTime = getValue();
          return createdTime
            ? getDateShortLabel("fr", createdTime)
            : t("missingData", { ns: "common" });
        },
        meta: {
          isInFilterBar: true,
          renderFilter: ({
            value,
            onChange,
          }: {
            value: RangeValues;
            onChange: (e: RangeValues) => void;
          }) => (
            <MonthPicker
              isRange
              label={t(
                "perimeter.sites.invoiceAndPaymentsDetails.accountingDocumentsTable.columns.accountingDocumentDate",
              )}
              onChange={onChange}
              value={value}
            >
              <MonthPicker.Trigger labelPosition="inside" size="sm" />
              <MonthPicker.Dropdown />
            </MonthPicker>
          ),
        },
      }),
      columnHelper.accessor("paymentDeadlineDate", {
        header: t(
          "perimeter.sites.invoiceAndPaymentsDetails.accountingDocumentsTable.columns.paymentDeadlineDate",
        ),
        enableSorting: false,
        cell: ({ getValue }) => {
          const createdTime = getValue();
          return createdTime
            ? getDateShortLabel("fr", createdTime)
            : t("missingData", { ns: "common" });
        },
      }),
      columnHelper.accessor("amount", {
        header: t(
          "perimeter.sites.invoiceAndPaymentsDetails.accountingDocumentsTable.columns.amount",
        ),
        enableSorting: false,
        cell: ({ getValue }) =>
          t("amount", {
            ns: "common",
            amount: getValue(),
            currency: "EUR",
            formatParams: {
              amount: {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              },
            },
          }),
      }),
      columnHelper.accessor("balanceCustomer", {
        header: t(
          "perimeter.sites.invoiceAndPaymentsDetails.accountingDocumentsTable.columns.customerBalance",
        ),
        enableSorting: false,
        cell: ({ getValue }) =>
          t("amount", {
            ns: "common",
            amount: getValue(),
            currency: "EUR",
            formatParams: {
              amount: {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              },
            },
          }),
      }),
      columnHelper.accessor("billPaymentStatus", {
        id: "billPaymentStatus",
        header: t(
          "perimeter.sites.invoiceAndPaymentsDetails.accountingDocumentsTable.columns.billPaymentStatus",
        ),
        cell: ({ getValue }) =>
          t(
            `perimeter.sites.invoiceAndPaymentsDetails.accountingDocumentsTable.billPaymentStatus.${getValue()}`,
          ),
        enableSorting: false,
        meta: {
          isInFilterBar: true,
          renderFilter: "multiSelect",
          possibleValues: BILL_PAYMENT_STATUSES.map((value) => ({
            label: t(
              `perimeter.sites.invoiceAndPaymentsDetails.accountingDocumentsTable.billPaymentStatus.${value}`,
            ),
            value,
          })),
        },
      }),
    ],
    [t],
  );

  return columns;
};
